import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { BookMarked, Hammer, LogOut, LibraryBig, Crown, Sparkles, CreditCard, Star, Trophy, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

function daysLeft(iso) {
  if (!iso) return 0;
  return Math.max(0, Math.ceil((new Date(iso) - new Date()) / 86400000));
}

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [stats, setStats] = useState({ maestria: 0, stelle: 0 });

  useEffect(() => {
    api.get("/libretto")
      .then((r) => setStats({ maestria: r.data.maestria, stelle: r.data.cfu_acquisiti }))
      .catch(() => {});
  }, [pathname]);

  const nav = [
    { to: "/", label: "Bottega", icon: LibraryBig },
    { to: "/registro", label: "Registro", icon: BookMarked },
    { to: "/account", label: "Il tuo posto", icon: CreditCard },
    ...(user?.role === "admin" ? [{ to: "/admin", label: "Admin", icon: ShieldCheck }] : []),
  ];

  const hasAccess = user?.has_access;
  const isTrial = hasAccess && user?.plan === "trial";
  const left = daysLeft(user?.access_until);

  return (
    <header className="sticky top-0 z-40 border-b border-border backdrop-blur-xl bg-background/90">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2.5 group shrink-0" data-testid="nav-logo">
          <Hammer className="h-6 w-6 text-primary" strokeWidth={1.5} />
          <span className="font-serif text-lg sm:text-xl font-bold tracking-tight">Bottega di Filosofia</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {nav.map((n) => {
            const active = pathname === n.to;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to === "/" ? "piano" : n.to.slice(1)}`}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-sm text-sm font-medium transition-colors ${
                  active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-secondary"
                }`}
              >
                <n.icon className="h-4 w-4" strokeWidth={1.5} />
                {n.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/registro" data-testid="nav-maestria" title="Maestria"
            className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary border border-border">
            <Trophy className="h-3.5 w-3.5 text-accent" strokeWidth={2} />
            <span className="text-xs font-bold tabular-nums">{stats.maestria.toLocaleString("it-IT")}</span>
          </Link>
          <Link to="/registro" data-testid="nav-stelle"
            className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary border border-border">
            <Star className="h-3.5 w-3.5 text-accent fill-accent" strokeWidth={1.5} />
            <span className="text-xs font-bold tabular-nums">{stats.stelle}</span>
          </Link>

          {hasAccess ? (
            isTrial ? (
              <Link to="/abbonamento" data-testid="nav-trial-badge">
                <Badge variant="outline" className="rounded-full border-accent/40 text-accent-foreground hover:bg-accent/10">
                  <Sparkles className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} /> {left}g in Bottega
                </Badge>
              </Link>
            ) : (
              <Link to="/account" data-testid="nav-plan-badge" className="hidden sm:inline-flex">
                <Badge className="rounded-full bg-accent text-accent-foreground cursor-pointer hover:bg-accent/90 transition-colors">
                  <Crown className="h-3.5 w-3.5 mr-1" strokeWidth={2} /> In Bottega
                </Badge>
              </Link>
            )
          ) : (
            <Button size="sm" onClick={() => navigate("/abbonamento")} data-testid="nav-subscribe-btn"
              className="rounded-full bg-primary hover:bg-primary/90 h-8">
              <Crown className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5} /> Entra
            </Button>
          )}
          <span className="hidden lg:inline text-sm text-muted-foreground">{user?.name}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { logout(); navigate("/login"); }}
            data-testid="logout-btn"
            className="text-muted-foreground hover:text-primary"
          >
            <LogOut className="h-4 w-4 sm:mr-1.5" strokeWidth={1.5} /> <span className="hidden sm:inline">Esci</span>
          </Button>
        </div>
      </div>
    </header>
  );
}
